'use client';

import { AutoResizeTextarea } from '@/components/ui/auto-resize-textarea';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { SearchModel, searchModels } from '@/config/site';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import {
  ArrowUpIcon,
  BookOpenIcon,
  CheckIcon,
  DatabaseIcon,
  GlobeIcon,
  LightbulbIcon,
  Loader2Icon,
  SparklesIcon,
  ZapIcon,
} from 'lucide-react';
import React, {
  forwardRef,
  useCallback,
  useMemo,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

export type SummaryStyle = 'academic' | 'simple';

export type SearchSource = 'pubmed' | 'openalex';
export const SOURCE_OPTIONS: Array<{
  value: SearchSource;
  label: string;
  description: string;
  icon: React.ElementType;
}> = [
  {
    value: 'pubmed',
    label: 'PubMed',
    description: '美国国立医学图书馆',
    icon: BookOpenIcon,
  },
  {
    value: 'openalex',
    label: 'OpenAlex',
    description: '全学科开放学术索引',
    icon: GlobeIcon,
  },
];
export const DEFAULT_SOURCES: SearchSource[] = ['pubmed', 'openalex'];

function formatSourcesLabel(selected: SearchSource[]): string {
  if (selected.length === 0) return '自动选择数据源';
  return selected
    .map((s) => SOURCE_OPTIONS.find((o) => o.value === s)?.label ?? s)
    .join(' + ');
}

function formatCompactSourcesLabel(selected: SearchSource[]): string {
  if (selected.length === 0) return '数据源';
  const hasPubmed = selected.includes('pubmed');
  const hasOpenalex = selected.includes('openalex');
  if (hasPubmed && hasOpenalex) return 'PubMed+OA';
  if (hasPubmed) return 'PubMed';
  if (hasOpenalex) return 'OpenAlex';
  return '数据源';
}

function formatModelLabel(mode: SearchModel, compact: boolean): string {
  const item = searchModels.find((entry) => entry.value === mode);
  if (!item) return '模式';
  if (!compact) return item.label;
  return item.label.replace('模式', '');
}

export interface SearchInputCardProps {
  value: string;
  onChange: (next: string) => void;
  onSubmit: () => void | Promise<void>;

  mode: SearchModel;
  onModeChange: (next: SearchModel) => void;

  style: SummaryStyle;
  onStyleChange: (next: SummaryStyle) => void;

  /** v3 的数据源多选（默认 pubmed + openalex） */
  sources?: SearchSource[];
  onSourcesChange?: (next: SearchSource[]) => void;

  placeholder?: string;
  minHeight?: number;
  maxHeight?: number;

  /** true 时禁用输入和交互（加载中 / 无内容 都可传入） */
  disabled?: boolean;
  /** 仅影响提交按钮图标（展示 spinner 还是 arrow） */
  loading?: boolean;

  /** 额外的右侧 slot（放在提交按钮前面） */
  rightSlot?: ReactNode;

  className?: string;
}

const searchModelIcons: Record<string, React.ElementType> = {
  Zap: ZapIcon,
  Sparkles: SparklesIcon,
  BrainCircuit: LightbulbIcon,
};

/**
 * 纯展示型搜索输入卡。首页 SearchTextarea 和 /search 追问 FollowUpInput 共用。
 * 业务（createChatSession / addChatMessage / router 跳转 / 点数校验）由调用方处理。
 */
const SearchInputCard = forwardRef<HTMLTextAreaElement, SearchInputCardProps>(
  function SearchInputCard(
    {
      value,
      onChange,
      onSubmit,
      mode,
      onModeChange,
      style,
      onStyleChange,
      sources,
      onSourcesChange,
      placeholder,
      minHeight = 44,
      maxHeight = 208,
      disabled,
      loading,
      rightSlot,
      className,
    },
    ref,
  ) {
    const isMobile = useIsMobile();
    const currentModelItem = useMemo(
      () => searchModels.find((item) => item.value === mode),
      [mode],
    );

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          if (!disabled) onSubmit();
        }
      },
      [onSubmit, disabled],
    );

    return (
      <form
        className={cn(
          'p-3 rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-background dark:bg-neutral-900/80 shadow-none',
          'focus-within:border-neutral-400 focus-within:ring-2 ring-neutral-300 dark:ring-neutral-700 dark:focus-within:border-neutral-600',
          className,
        )}
      >
        <AutoResizeTextarea
          ref={ref}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          minHeight={minHeight}
          maxHeight={maxHeight}
          className="w-full font-normal bg-transparent outline-hidden! placeholder:text-muted-foreground focus-visible:outline-hidden resize-none text-sm border-0 ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 p-0"
        />
        <div className="flex items-center gap-2 pt-2">
          <div className="min-w-0 flex-1 overflow-hidden">
            <div className="flex items-center gap-1.5 overflow-x-auto whitespace-nowrap pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:gap-2 sm:pb-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 rounded-full px-2.5 text-[11px] shadow-none text-foreground sm:h-8 sm:px-3 sm:text-xs"
                >
                  {currentModelItem?.icon &&
                    (() => {
                      const Icon = searchModelIcons[currentModelItem.icon];
                      return Icon ? <Icon className="w-3.5 h-3.5" /> : null;
                    })()}
                  {formatModelLabel(mode, !!isMobile)}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-72 p-2 rounded-xl"
                align="start"
                sideOffset={8}
              >
                {searchModels.map((item) => {
                  const Icon = searchModelIcons[item.icon];
                  const active = mode === item.value;
                  return (
                    <DropdownMenuItem
                      key={item.value}
                      onSelect={() => onModeChange(item.value as SearchModel)}
                      className="flex items-start gap-3 rounded-xl px-3 py-2.5 cursor-pointer focus:bg-muted"
                    >
                      {Icon && (
                        <Icon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-semibold text-foreground">
                            {item.label}
                          </span>
                          {item.value === 'deep' && (
                            <span className="text-[10px] font-semibold rounded-lg px-1 py-0.5 bg-red-500/10 text-red-600 dark:bg-red-400/10 dark:text-red-300">
                              NEW
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {item.description}
                        </div>
                      </div>
                      {active && (
                        <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-foreground" />
                      )}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {sources && onSourcesChange && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 max-w-full rounded-full px-2.5 text-[11px] shadow-none text-foreground sm:h-8 sm:px-3 sm:text-xs"
                >
                  <DatabaseIcon className="w-3.5 h-3.5" />
                  <span className="max-w-28 truncate sm:max-w-none">
                    {isMobile
                      ? formatCompactSourcesLabel(sources)
                      : formatSourcesLabel(sources)}
                  </span>
                </Button>
              </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-72 p-2 rounded-xl bg-card"
                  align="start"
                  sideOffset={8}
                >
                  {SOURCE_OPTIONS.map((item) => {
                    const checked = sources.includes(item.value);
                    const Icon = item.icon;
                    return (
                      <DropdownMenuItem
                        key={item.value}
                        onSelect={(e) => {
                          // 不让点击关闭菜单（多选场景）
                          e.preventDefault();
                          const next = checked
                            ? sources.filter((s) => s !== item.value)
                            : Array.from(new Set([...sources, item.value]));
                          onSourcesChange(next);
                        }}
                        className="flex items-start gap-3 rounded-xl px-3 py-2.5 cursor-pointer focus:bg-muted"
                      >
                        <Icon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-foreground">
                            {item.label}
                          </div>
                          <div className="mt-0.5 text-xs text-muted-foreground">
                            {item.description}
                          </div>
                        </div>
                        {checked && (
                          <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-foreground" />
                        )}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <Switch
              name="search-style"
              size="small"
              className="shrink-0"
              onValueChange={(v) => {
                if (v === 'academic' || v === 'simple') onStyleChange(v);
              }}
            >
              <Switch.Control
                defaultChecked={style === 'academic'}
                label={isMobile ? '学术' : '学术风格'}
                value="academic"
              />
              <Switch.Control
                defaultChecked={style === 'simple'}
                label={isMobile ? '科普' : '科普风格'}
                value="simple"
              />
            </Switch>
            </div>
          </div>

          {rightSlot}

          <Button
            type="button"
            size="icon"
            color="primary"
            aria-label="发送"
            className="h-8 w-8 shrink-0 rounded-full"
            disabled={disabled}
            onClick={(e) => {
              e.preventDefault();
              onSubmit();
            }}
          >
            {loading ? (
              <Loader2Icon className="w-4 h-4 animate-spin" />
            ) : (
              <ArrowUpIcon className="w-4 h-4" />
            )}
          </Button>
        </div>
      </form>
    );
  },
);

export default SearchInputCard;
